/* eslint-disable no-use-before-define */
import { ref, onMounted, onUnmounted } from 'vue';
import { throttle } from 'throttle-debounce';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function useAnimation(refreshDelay = 0) {
  const resizeObserver = ref<ResizeObserver | null>(null);
  const resizeObserverElements = ref<Array<Element | HTMLBodyElement | HTMLElement>>([]);
  const storedAnimations = ref<Array<gsap.core.Tween | gsap.core.Timeline | ScrollTrigger>>([]);

  const storedMatchMedia = ref<gsap.MatchMedia[]>([]);

  onMounted(() => {
    createResizeObserver();
    observeResizeOf(document.querySelector('body'));
  });

  onUnmounted(() => {
    clearAnimations();
    resizeObserverElements.value.forEach((element) => {
      if (resizeObserver.value) {
        resizeObserver.value.unobserve(element);
      }
    });
  });

  const createResizeObserver = () => {
    if (resizeObserver.value) return;
    const callback = throttle(700, () => {
      if (refreshDelay) {
        setTimeout(() => {
          refreshAnimations();
        }, refreshDelay);
      } else {
        refreshAnimations();
      }
    });
    if (typeof ResizeObserver === 'function') {
      resizeObserver.value = new ResizeObserver(callback);
    }
  };

  const observeResizeOf = (element: Element | null) => {
    if (!element || resizeObserverElements.value.includes(element) || !resizeObserver.value) return;
    resizeObserver.value.observe(element);
    resizeObserverElements.value.push(element);
  };

  const createTimeline = (vars: gsap.TimelineVars) => {
    const tl = gsap.timeline(vars);
    storedAnimations.value.push(tl);
    checkCustomScroller(vars.scrollTrigger);
    return tl;
  };

  const createTweenFrom = (targets: gsap.TweenTarget, vars: gsap.TweenVars) => {
    const tween = gsap.from(targets, vars);
    storedAnimations.value.push(tween);
    checkCustomScroller(vars.scrollTrigger);
    return tween;
  };

  const createTweenTo = (targets: gsap.TweenTarget, vars: gsap.TweenVars) => {
    const tween = gsap.to(targets, vars);
    storedAnimations.value.push(tween);
    checkCustomScroller(vars.scrollTrigger);
    return tween;
  };

  const createScrollTrigger = (config: ScrollTrigger.StaticVars) => {
    const scrollTrigger = ScrollTrigger.create(config);
    storedAnimations.value.push(scrollTrigger);
    checkCustomScroller(config.scroller);
    return scrollTrigger;
  };

  const checkCustomScroller = (vars: ScrollTrigger.Vars | undefined | gsap.DOMTarget) => {
    if (!isScrollTriggerVars(vars)) return;
    const scroller =
      typeof vars.scroller === 'string' ? document.querySelector(vars.scroller) : vars.scroller;
    observeResizeOf(scroller as Element);
  };
  const isScrollTrigger = (element: unknown): element is ScrollTrigger =>
    element instanceof ScrollTrigger;

  const isScrollTriggerVars = (element: unknown): element is ScrollTrigger.Vars => {
    if (!element) return false;
    const vars = element as ScrollTrigger.Vars;
    return vars.scroller !== undefined;
  };

  const clearAnimations = () => {
    storedAnimations.value.forEach((animation) => {
      if (animation && isScrollTrigger(animation)) {
        animation.kill(true);
      } else if (isScrollTrigger(animation?.scrollTrigger)) {
        animation.scrollTrigger.kill(true);
      }
    });
    storedAnimations.value = [];

    storedMatchMedia.value.forEach((matchMedia) => {
      matchMedia?.kill();
    });

    storedMatchMedia.value = [];
  };
  const refreshAnimations = () => {
    storedAnimations.value.forEach((animation) => {
      if (animation && isScrollTrigger(animation)) {
        animation.refresh();
      } else if (isScrollTrigger(animation?.scrollTrigger)) {
        animation.scrollTrigger.refresh();
      }
    });
  };

  const createMatchMedia = (entries?: Record<string, (context?: any) => any | void>) => {
    const matchMedia = gsap.matchMedia();
    if (entries) {
      Object.keys(entries).forEach((conditions) => {
        matchMedia.add(conditions, entries[conditions]);
      });
    }
    storedMatchMedia.value.push(matchMedia);
    return matchMedia;
  };

  return {
    createTimeline,
    createTweenFrom,
    createTweenTo,
    createScrollTrigger,
    createMatchMedia,
  };
}
